import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';
const TenantContext = createContext({});

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { user, session, isAuthenticated } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [homeTenantId, setHomeTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewTenant, setIsNewTenant] = useState(false);
  const [allTenants, setAllTenants] = useState([]);
  const provisionAttempts = React.useRef(0);

  const fetchInProgress = React.useRef(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Debounce — auth fires multiple events during signup
      const timer = setTimeout(() => {
        if (!fetchInProgress.current) {
          fetchTenant();
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setTenant(null);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchTenant = async () => {
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    try {
      // First try to get existing tenant
      const { data, error } = await supabase
        .from('tenant_members')
        .select(`
          role,
          tenants (
            id,
            business_name,
            business_type,
            subdomain,
            phone,
            email,
            status,
            plan,
            trial_ends_at,
            created_at,
            enabled_features
          )
        `)
        .eq('user_id', user.id)
        .maybeSingle();

      // Super admin override — check BEFORE the tenant_members check
      // so it works even if SA has no tenant_members row
      var isSA = user?.email?.toLowerCase() === 'ivanjackm@protonmail.com';
      var overrideId = null;
      try { overrideId = localStorage.getItem('sa_tenant_override'); } catch(e) {}

      if (isSA && overrideId) {
        var { data: overrideTenant, error: oErr } = await supabase
          .from('tenants')
          .select('id, business_name, business_type, subdomain, phone, email, status, plan, trial_ends_at, created_at, enabled_features, custom_slug')
          .eq('id', parseInt(overrideId))
          .single();
        if (overrideTenant && !oErr) {
          setTenant({ ...overrideTenant, userRole: 'owner' });
          // Also load all tenants for switcher
          var { data: allT2 } = await supabase.from('tenants').select('id, business_name, business_type').order('id');
          if (allT2) setAllTenants(allT2);
          setLoading(false);
          fetchInProgress.current = false;
          return;
        } else {
          // Override tenant not found, clear it
          localStorage.removeItem('sa_tenant_override');
        }
      }

      if (data && data.tenants) {
        var homeTenant = { ...data.tenants, userRole: data.role };
        setHomeTenantId(data.tenants.id);

        // Legacy override check (fallback)
        if (isSA && overrideId && overrideId !== String(data.tenants.id)) {
          var { data: overrideTenant2, error: oErr2 } = await supabase
            .from('tenants')
            .select('id, business_name, business_type, subdomain, phone, email, status, plan, trial_ends_at, created_at, enabled_features, custom_slug')
            .eq('id', overrideId)
            .single();
          if (overrideTenant2 && !oErr2) {
            setTenant({ ...overrideTenant2, userRole: 'owner' });
          } else {
            localStorage.removeItem('sa_tenant_override');
            setTenant(homeTenant);
          }
        } else {
          setTenant(homeTenant);
        }

        // Load all tenants for super admin switcher
        if (isSA) {
          var { data: allT } = await supabase
            .from('tenants')
            .select('id, business_name, business_type')
            .order('id');
          if (allT) setAllTenants(allT);
        }

        setIsNewTenant(false);
        setLoading(false);
        return;
      }

      // No tenant found - auto-provision via backend
      console.log('No tenant found, auto-provisioning...');
      
      // Prevent infinite provisioning loop
      provisionAttempts.current += 1;
      if (provisionAttempts.current > 2) {
        console.error('Auto-provisioning failed after multiple attempts');
        setLoading(false);
        return;
      }

      // Get fresh session token (the one from context may be stale)
      const { data: freshSession } = await supabase.auth.getSession();
      const token = freshSession?.session?.access_token;
      if (!token) {
        console.error('No valid auth token for provisioning');
        setLoading(false);
        return;
      }

      // Get signup metadata from Supabase user object
      const { data: freshUser } = await supabase.auth.getUser(token);
      const userMeta = freshUser?.user?.user_metadata || user?.user_metadata || {};

      const res = await fetch(API_URL + '/api/auth/setup-tenant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({
          businessName: userMeta.business_name || userMeta.businessName || '',
          businessType: userMeta.business_type || userMeta.businessType || 'other',
          fullName: userMeta.full_name || '',
          phone: userMeta.phone || '',
        }),
      });

      const result = await res.json();
      if (result.success) {
        setTenant({
          ...result.tenant,
          userRole: result.role,
        });
        setHomeTenantId(result.tenant.id);
        setIsNewTenant(result.isNew);
      } else {
        throw new Error(result.error || 'Failed to setup tenant');
      }
    } catch (err) {
      console.error('Error fetching tenant:', err.message);
      setTenant(null);
    } finally {
      fetchInProgress.current = false;
      setLoading(false);
    }
  };

  // Super admin: switch to a different tenant
  const switchTenant = async (tenantId) => {
    if (!tenantId) {
      // Switch back to home
      localStorage.removeItem('sa_tenant_override');
    } else {
      localStorage.setItem('sa_tenant_override', tenantId.toString());
    }
    setLoading(true);
    await fetchTenant();
  };

  const updateTenant = async (updates) => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('id', tenant.id)
        .select()
        .single();
      if (error) throw error;
      setTenant(prev => ({ ...prev, ...data }));
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err.message };
    }
  };

  const isSuperAdmin = user?.email?.toLowerCase() === 'ivanjackm@protonmail.com';
  var isViewingOther = isSuperAdmin && tenant && homeTenantId && tenant.id !== homeTenantId;

  const enabledFeatures = tenant?.enabled_features || [];
  const hasFeature = (feature) => {
    if (!enabledFeatures || enabledFeatures.length === 0) return true;
    return enabledFeatures.includes(feature);
  };

  const value = {
    tenant,
    loading,
    updateTenant,
    refreshTenant: fetchTenant,
    tenantId: tenant?.id || null,
    businessType: tenant?.business_type || 'other',
    businessName: tenant?.business_name || 'My Business',
    subdomain: tenant?.subdomain || null,
    userRole: tenant?.userRole || 'owner',
    isNewTenant,
    isSuperAdmin,
    enabledFeatures,
    hasFeature,
    // Super admin tenant switching
    switchTenant,
    allTenants,
    homeTenantId,
    isViewingOther,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantProvider;
